import { api } from ".";
import {
  EdiCsvDownloadKind,
  EdiSplitProcessMode,
  IEdiConversionFile,
  IEdiSplitBatch,
  IEdiUploadMapping,
} from "../../types/ediConversionFile";

// Response from POST /edi-conversion/upload-init — step 1 of the direct-to-S3 upload flow.
// The client then PUTs the file bytes to `uploadUrl` (and `upload837Url` if present), and
// finally calls /upload-complete with the uploadBatchId to kick off async parsing.
export interface IEdiUploadInitResponse {
  uploadBatchId: string;
  ediConversionFileIds: string[];
  uploadUrl: string;
  upload837Url?: string;
  uploadExpiresAt: string;
}

// One provider in the "Reprocess all" picker — a provider that has EDI files, with the aggregate
// stats shown in the modal once it is selected.
export interface IProviderReprocessOption {
  providerId: string;
  providerName: string;
  fileCount: number;
  totalRecords: number;
  oldestFileAt: string;
  newestFileAt: string;
}

// Outcome of a bulk per-provider reprocess enqueue.
export interface IBulkReprocessResult {
  providerId: string;
  totalFiles: number;
  queued: number;
  skippedNoOriginal: number;
}

// CLE-2483 — a standalone 837 ingest (uploaded as its own operation, decoupled from any 835).
export interface IEdi837Ingest {
  id: string;
  accountId: string | null;
  s3Key: string;
  displayName: string | null;
  fileName: string | null;
  providerId: string | null;
  providerName: string | null;
  status: "PARSING" | "COMMITTED" | "REJECTED";
  clmCount: number | null;
  submissionCount: number;
  matchedCount: number;
  lastReprocessedAt: string | null;
  createdAt: string;
}

// Response from POST /edi-conversion/837/upload-init (direct-to-S3). Client PUTs to uploadUrl then
// calls /837/upload-complete with the edi837FileIngestId.
export interface IEdi837UploadInitResponse {
  edi837FileIngestId: string;
  uploadUrl: string;
  uploadExpiresAt: string;
}

const ediConversionQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    getAllEdiConversionFiles: builder.query<
      { data: IEdiConversionFile[]; message: string },
      { search?: string }
    >({
      query: (params) => ({
        url: "/edi-conversion",
        params,
      }),
      providesTags: ["EdiConversion"],
    }),
    getEdiConversionFileById: builder.query<
      { data: IEdiConversionFile; message: string },
      string
    >({
      query: (id) => `/edi-conversion/${id}`,
    }),
    // CLE-2236: one upload now carries 1–3 provider-mapping rules and fans out into one
    // EdiConversionFile per rule, so the response is an array.
    convertAndUploadEdi: builder.mutation<
      { data: IEdiConversionFile[]; message: string; warnings?: string[] },
      {
        files: File[];
        displayName: string;
        description?: string;
        mappings: IEdiUploadMapping[];
        files837?: File[];
      }
    >({
      query: ({ files, displayName, description, mappings, files837 }) => {
        const formData = new FormData();
        files.forEach((f) => formData.append("files", f));
        if (files837) {
          files837.forEach((f) => formData.append("files837", f));
        }
        formData.append("displayName", displayName);
        if (description) {
          formData.append("description", description);
        }
        formData.append("mappings", JSON.stringify(mappings));

        return {
          url: "/edi-conversion/convert",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["EdiConversion"],
    }),
    getEdiConversionFileDownloadUrl: builder.query<
      {
        data: { downloadUrl: string; kind: EdiCsvDownloadKind };
        message: string;
      },
      string
    >({
      query: (id) => `/edi-conversion/${id}/download`,
    }),
    // Deleting or reprocessing a split piece changes the "Split 835 Files" list too (CLE-2921).
    deleteEdiConversionFile: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `/edi-conversion/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["EdiConversion", "EdiSplits"],
    }),
    reprocessEdiFile: builder.mutation<
      { data: IEdiConversionFile; message: string },
      string
    >({
      query: (id) => ({
        url: `/edi-conversion/${id}/reprocess`,
        method: "POST",
      }),
      invalidatesTags: ["EdiConversion", "EdiSplits"],
    }),
    // CLE-2479 — edit an already-processed file's provider-mapping (provider / claim type / POS /
    // taxonomy). Persists the mapping only; the caller triggers a reprocess separately to apply it.
    updateEdiFileMapping: builder.mutation<
      { data: IEdiConversionFile; message: string },
      { id: string; mapping: IEdiUploadMapping }
    >({
      query: ({ id, mapping }) => ({
        url: `/edi-conversion/${id}/mapping`,
        method: "PATCH",
        body: { mapping },
      }),
      invalidatesTags: ["EdiConversion"],
    }),
    // Providers that have EDI files, each with file count / total records / date span — powers the
    // "Reprocess all for a provider" modal's picker and at-a-glance stats.
    getProviderReprocessOptions: builder.query<
      { data: IProviderReprocessOption[]; message: string },
      void
    >({
      query: () => "/edi-conversion/provider-reprocess-options",
      providesTags: ["EdiConversion"],
    }),
    // Bulk-enqueue a reprocess for every EDI file belonging to a provider, oldest → newest.
    reprocessAllForProvider: builder.mutation<
      { data: IBulkReprocessResult; message: string },
      { providerId: string }
    >({
      query: (body) => ({
        url: "/edi-conversion/reprocess-all",
        method: "POST",
        body,
      }),
      invalidatesTags: ["EdiConversion"],
    }),
    runAiAnalysis: builder.mutation<
      { data: { claimCount: number }; message: string },
      string
    >({
      query: (id) => ({
        url: `/edi-conversion/${id}/run-ai-analysis`,
        method: "POST",
      }),
      invalidatesTags: ["EdiConversion"],
    }),
    // Direct-upload step 1: allocate pre-signed S3 PUT URLs without sending bytes through
    // the API. Client then PUTs to uploadUrl and calls /upload-complete.
    initiateEdiUploadBatch: builder.mutation<
      { data: IEdiUploadInitResponse; message: string },
      {
        displayName: string;
        description?: string;
        originalFilename: string;
        fileSizeBytes: number;
        has837?: boolean;
        // When has837 is set, these let the backend allocate an S3 key that keeps the real
        // extension (e.g. .zip vs .837 vs .txt) and re-check the 837's size against the cap
        // after the client PUT completes.
        original837Filename?: string;
        file837SizeBytes?: number;
        mappings: IEdiUploadMapping[];
      }
    >({
      query: (body) => ({
        url: "/edi-conversion/upload-init",
        method: "POST",
        body,
      }),
    }),
    // Direct-upload step 2: HEAD-verify the S3 object and kick off async parsing.
    confirmEdiUploadBatch: builder.mutation<
      { message: string },
      { uploadBatchId: string }
    >({
      query: (body) => ({
        url: "/edi-conversion/upload-complete",
        method: "POST",
        body,
      }),
      invalidatesTags: ["EdiConversion"],
    }),

    // ── CLE-2483: standalone 837 upload + management (a separate operation from the 835 upload) ──
    getAll837Ingests: builder.query<
      { data: IEdi837Ingest[]; message: string },
      { search?: string } | void
    >({
      query: (args) => ({
        url: "/edi-conversion/837",
        params: args && args.search ? { search: args.search } : undefined,
      }),
      providesTags: ["EdiConversion"],
    }),
    // 837 direct-upload step 1: allocate a presigned S3 PUT URL + create the Edi837FileIngest.
    initiate837Upload: builder.mutation<
      { data: IEdi837UploadInitResponse; message: string },
      {
        displayName: string;
        originalFilename: string;
        fileSizeBytes: number;
        mappings: IEdiUploadMapping[];
      }
    >({
      query: (body) => ({
        url: "/edi-conversion/837/upload-init",
        method: "POST",
        body,
      }),
    }),
    // 837 direct-upload step 2: verify the S3 object + enqueue the ingest_837 job.
    confirm837Upload: builder.mutation<
      { message: string },
      { edi837FileIngestId: string }
    >({
      query: (body) => ({
        url: "/edi-conversion/837/upload-complete",
        method: "POST",
        body,
      }),
      invalidatesTags: ["EdiConversion"],
    }),
    reprocess837Ingest: builder.mutation<
      { data: IEdi837Ingest; message: string },
      string
    >({
      query: (id) => ({
        url: `/edi-conversion/837/${id}/reprocess`,
        method: "POST",
      }),
      invalidatesTags: ["EdiConversion"],
    }),
    delete837Ingest: builder.mutation<{ message: string }, string>({
      query: (id) => ({ url: `/edi-conversion/837/${id}`, method: "DELETE" }),
      invalidatesTags: ["EdiConversion"],
    }),

    // ── CLE-2921: "Split 835 Files" (SuperAdmin only) ──
    // Split batches, newest first (max 100), each with its pieces ordered by splitIndex.
    getEdiSplits: builder.query<
      { data: IEdiSplitBatch[]; message: string },
      void
    >({
      query: () => "/edi-conversion/splits",
      providesTags: ["EdiSplits"],
    }),
    // Queue a split of one conversion file. Answers 202 with the batch in 'queued'; the work runs
    // in the EDI processing job queue.
    createEdiSplit: builder.mutation<
      { data: IEdiSplitBatch; message: string },
      {
        sourceEdiConversionFileId: string;
        recordsPerPiece: number;
        processMode: EdiSplitProcessMode;
      }
    >({
      query: (body) => ({
        url: "/edi-conversion/splits",
        method: "POST",
        body,
      }),
      invalidatesTags: ["EdiConversion", "EdiSplits"],
    }),
    // Start the next held piece of a completed batch, or every held piece. `started` lists the ids
    // of the pieces that started (empty when nothing was held).
    processEdiSplitPieces: builder.mutation<
      { data: { started: string[] }; message: string },
      { batchId: string; pieces: "next" | "all" }
    >({
      query: ({ batchId, pieces }) => ({
        url: `/edi-conversion/splits/${batchId}/process`,
        method: "POST",
        body: { pieces },
      }),
      invalidatesTags: ["EdiConversion", "EdiSplits"],
    }),
    // Start the first-time parse of one held file (uploadStatus 'held'). 409 when it is not held.
    processHeldEdiFile: builder.mutation<
      { data: IEdiConversionFile; message: string },
      string
    >({
      query: (id) => ({
        url: `/edi-conversion/${id}/process`,
        method: "POST",
      }),
      invalidatesTags: ["EdiConversion", "EdiSplits"],
    }),
  }),
});

export const {
  useGetAllEdiConversionFilesQuery,
  useGetEdiConversionFileByIdQuery,
  useConvertAndUploadEdiMutation,
  useGetEdiConversionFileDownloadUrlQuery,
  useDeleteEdiConversionFileMutation,
  useReprocessEdiFileMutation,
  useUpdateEdiFileMappingMutation,
  useGetProviderReprocessOptionsQuery,
  useReprocessAllForProviderMutation,
  useRunAiAnalysisMutation,
  useInitiateEdiUploadBatchMutation,
  useConfirmEdiUploadBatchMutation,
  useGetAll837IngestsQuery,
  useInitiate837UploadMutation,
  useConfirm837UploadMutation,
  useReprocess837IngestMutation,
  useDelete837IngestMutation,
  useGetEdiSplitsQuery,
  useCreateEdiSplitMutation,
  useProcessEdiSplitPiecesMutation,
  useProcessHeldEdiFileMutation,
} = ediConversionQueries;
